import L from "leaflet";
import type { CanvasPinIcon } from "./CanvasPinIcon";
import type { PinProperty } from "../propertySelection/pinProperty";

/**
 * Leaflet が公開していない Path / Canvas レンダラの内部プロパティ。
 * Canvas レンダラは描画・ヒットテストのたびにレイヤーのこれらを参照する。
 */
interface CanvasPathInternals {
  _point: L.Point;
  _pxBounds: L.Bounds;
  _renderer: { _ctx?: CanvasRenderingContext2D | null; _drawing?: boolean };
}

/**
 * ピンを重ねる段。数が大きいほど上に描き、クリックも上の段のピンが受け取る。
 * 同じ段の中では、レンダラに追加した順（後に追加したものが上）
 */
export const PIN_DRAW_LEVEL = {
  /** 種類ごとのピン（検索結果・保存した物件リストなど） */
  normal: 0,
  /** 物件一覧に入っている物件の青ピン。元のピンの上に重ねる */
  listed: 1,
  /** 選択中（ポップアップを開いている）ピン */
  selected: 2,
} as const;

export interface CanvasPinMarkerOptions extends L.PathOptions {
  icon: CanvasPinIcon;
  /** 選択中（ポップアップを開いている間）に使う見た目。無ければ icon のまま */
  selectedIcon?: CanvasPinIcon;
  /** 重ねる段（PIN_DRAW_LEVEL）。既定は normal。選択中は常に selected */
  drawLevel?: number;
  /** ピンが表している物件。物件一覧の行と突き合わせるのに使う（getPinProperty） */
  property?: PinProperty;
}

/**
 * Canvas レンダラ上にピン画像を描くマーカー。
 *
 * L.Marker はピン1本ごとに DOM 要素（画像・影）を作るため、数千件になるとパンやズームで
 * 地図が固まる。こちらは1枚の canvas にまとめて描くので件数が増えても重くならない。
 * L.CircleMarker を土台にしているため、bindPopup・click などのイベント・LayerGroup は
 * L.Marker と同じように使える。
 *
 * ポップアップを開いている間は「選択中」になり、selectedIcon で一番上の段に描く。
 *
 * 地図に載せる時は CanvasPinRenderer を renderer に指定すること
 * （既定の Canvas レンダラは下にある MapLibre レイヤーのクリックを奪ってしまう）。
 */
export class CanvasPinMarker extends L.CircleMarker {
  declare options: CanvasPinMarkerOptions & L.CircleMarkerOptions;

  /** 印刷出力時の拡大率。通常表示は 1 */
  private printScale = 1;

  private selected = false;

  constructor(latlng: L.LatLngExpression, options: CanvasPinMarkerOptions) {
    super(latlng, {
      interactive: true,
      ...options,
      // 半径は描画に使わない（_updateBounds で上書き）。ただし L.Map が 10 以下のものを
      // 「マーカー」とみなしてイベントの latlng をピンの地点に揃えるため、小さい値にしておく
      radius: 1,
      stroke: false,
      fill: false,
      // L.Marker と同じく、ピンのクリックを地図のクリック（地番検索など）として扱わない
      bubblingMouseEvents: false,
    });
    this.on("popupopen", () => this.setSelected(true));
    this.on("popupclose", () => this.setSelected(false));
  }

  /**
   * 印刷出力用にピンを拡大する。1 で元に戻る。
   */
  setPrintScale(scale: number): this {
    this.printScale = scale;
    this.redraw();
    return this;
  }

  getPrintScale(): number {
    return this.printScale;
  }

  /**
   * ふだんの見た目を差し替える（一覧に入った時など）。
   * 開いているポップアップは新しいピンの頭の上に付け直す。
   */
  setIcon(icon: CanvasPinIcon): this {
    if (this.options.icon === icon) return this;
    this.options.icon = icon;
    this.refresh();
    return this;
  }

  /** 選択中の見た目を差し替える。undefined ならふだんの見た目のまま一番上に描く */
  setSelectedIcon(icon: CanvasPinIcon | undefined): this {
    if (this.options.selectedIcon === icon) return this;
    this.options.selectedIcon = icon;
    if (this.selected) this.refresh();
    return this;
  }

  /** いま描いている見た目（選択中なら selectedIcon） */
  getIcon(): CanvasPinIcon {
    if (this.selected && this.options.selectedIcon) {
      return this.options.selectedIcon;
    }
    return this.options.icon;
  }

  /**
   * 選択中にする・戻す。ポップアップの開閉では自動で切り替わるので、
   * ポップアップを持たないピン（ダイアログを開くものなど）の時だけ呼ぶ
   */
  setSelected(selected: boolean): this {
    if (this.selected === selected) return this;
    this.selected = selected;
    this.refresh();
    return this;
  }

  isSelected(): boolean {
    return this.selected;
  }

  /** 重ねる段（PIN_DRAW_LEVEL） */
  getDrawLevel(): number {
    return this.selected
      ? PIN_DRAW_LEVEL.selected
      : this.options.drawLevel ?? PIN_DRAW_LEVEL.normal;
  }

  private refresh(): void {
    this.redraw();
    if (this.isPopupOpen()) this.getPopup()?.update();
  }

  /** ピン画像が占める範囲（レイヤー座標の px）を返す */
  private getImageBounds(point: L.Point): L.Bounds {
    const { size, anchor } = this.getIcon().options;
    const scale = this.printScale;
    const topLeft = L.point(
      point.x - anchor[0] * scale,
      point.y - anchor[1] * scale
    );
    return L.bounds(topLeft, topLeft.add([size[0] * scale, size[1] * scale]));
  }

  /** 影や外縁のはみ出しを含めた、ビットマップを描く範囲（レイヤー座標の px） */
  private getBitmapBounds(point: L.Point): L.Bounds {
    const bounds = this.getImageBounds(point);
    const bleed = this.getIcon().bleed * this.printScale;
    const min = bounds.min ?? point;
    const max = bounds.max ?? point;
    return L.bounds(min.subtract([bleed, bleed]), max.add([bleed, bleed]));
  }

  // Canvas レンダラが再描画範囲・画面外判定に使う範囲を、影まで含めたピン画像の大きさにする
  _updateBounds(): void {
    const internals = this as unknown as CanvasPathInternals;
    internals._pxBounds = this.getBitmapBounds(internals._point);
  }

  // 円の代わりにピンのビットマップを描く。
  // Leaflet の円と同じく、レンダラがまとめて描いている最中だけ描く（redraw の直後に
  // 重ね順を無視して描いてしまわないように）
  _updatePath(): void {
    const internals = this as unknown as CanvasPathInternals;
    const { _ctx: ctx, _drawing: drawing } = internals._renderer;
    if (!ctx || !drawing) return;
    const bitmap = this.getIcon().getBitmap(
      (window.devicePixelRatio || 1) * this.printScale
    );
    if (!bitmap) return;

    const bounds = this.getBitmapBounds(internals._point);
    const min = bounds.min ?? internals._point;
    const max = bounds.max ?? internals._point;
    ctx.drawImage(
      bitmap,
      Math.round(min.x),
      Math.round(min.y),
      max.x - min.x,
      max.y - min.y
    );
  }

  // クリック・ホバーの当たり判定をピン本体の矩形にする（影・外縁は含めない）
  _containsPoint(point: L.Point): boolean {
    const internals = this as unknown as CanvasPathInternals;
    return this.getImageBounds(internals._point).contains(point);
  }

  // L.Marker の popupAnchor と同じく、ポップアップをピンの頭の上に出す
  _getPopupAnchor(): L.Point {
    const [x, y] = this.getIcon().options.popupAnchor;
    return L.point(x * this.printScale, y * this.printScale);
  }
}
